import React, { ComponentProps } from 'react';
import { compose } from 'recompose';
import * as withContainer from '../../graphql/containers';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import { useDimensionColumns } from '../../utils/DimensionSets';
import Formatters from '../../components/grid-addons/formatters';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import HeaderTitle from '../../components/connectedForm/HeaderTitle';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { Button } from '../../components/connectedForm/Button';
import BreadCrumbs from '../../components/connectedForm/BreadCrumbs';
import GridContainer from '../../components/connectedForm/GridContainer';
import EditableDataGrid from '../../components/EditableDataGrid';
import { EntityType } from '../../@types/entityTypes';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { IEditableDataGridProps } from '../../components/EditableDataGrid/EditableDataGrid';

const getFormId = (props: { formId?: number; match: { params: any } }) => {
  const {
    formId,
    match: { params },
  } = props;
  return formId || params.id;
};

const COLUMN_META: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Date',
    key: 'documentDate',
    width: 120,
    formatter: Formatters.DateFormatter,
    resizable: false,
  },
  {
    name: 'Nominal',
    key: 'nominal.id',
    width: 125,
    resizable: true,
    formatter: Formatters.LinkFormatter,
    options: {
      url: '/Nominal/Accounts/Details/',
      entity: { type: 'Nominal' } as EntityType,
      linkField: 'rowData.nominal.accountNo',
      title: 'View nominal',
      editInNewTab: true,
    },
  },
  {
    name: 'Description',
    key: 'nominal.name',
    resizable: true,
  },
  {
    name: 'Bank',
    key: 'nominal.bankAccount',
    width: 50,
    formatter: Formatters.BooleanFormatter,
    resizable: false,
  },
  {
    name: 'Type',
    key: 'nominal.accountTypeDisplay',
    width: 150,
    resizable: true,
  },
  {
    name: 'Financial Period',
    key: 'financialPeriod.name',
    resizable: true,
  },
  {
    name: 'Debit',
    key: 'baseDebitAmount',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
  {
    name: 'Credit',
    key: 'baseCreditAmount',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
];

const TRANSACTION_COLUMN_META: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Date',
    key: 'documentDate',
    width: 120,
    formatter: Formatters.DateFormatter,
  },
  {
    name: 'Customer',
    key: 'customer.id',
    width: 120,
    formatter: Formatters.LinkFormatter,
    options: {
      url: '/Customers/Customer/Details/',
      entity: { type: 'Customer' } as EntityType,
      linkField: 'rowData.customer.reference',
      title: 'View customer',
      editInNewTab: true,
    },
  },
  {
    name: 'Source No',
    key: 'documentNumber',
    width: 120,
  },
  {
    name: 'Reference',
    key: 'documentReference',
  },
  {
    name: 'Currency',
    key: 'currency.description',
    width: 120,
  },
  {
    name: 'Currency Amount',
    key: 'amount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  {
    name: 'Goods',
    key: 'baseGoodsAmount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  {
    name: 'VAT',
    key: 'baseVatAmount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  {
    name: 'Total Amount',
    key: 'baseAmount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
];

const PURCHASE_TRANSACTION_COLUMN_META: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Date',
    key: 'documentDate',
    width: 120,
    formatter: Formatters.DateFormatter,
  },
  {
    name: 'Supplier',
    key: 'supplier.id',
    width: 120,
    formatter: Formatters.LinkFormatter,
    options: {
      url: '/Suppliers/Supplier/Details/',
      entity: { type: 'Supplier' } as EntityType,
      linkField: 'rowData.supplier.reference',
      title: 'View supplier',
      editInNewTab: true,
    },
  },
  {
    name: 'Source No',
    key: 'documentNumber',
    width: 120,
  },
  {
    name: 'Reference',
    key: 'documentReference',
  },
  {
    name: 'Currency',
    key: 'currency.description',
    width: 120,
  },
  {
    name: 'Currency Amount',
    key: 'amount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  {
    name: 'Goods',
    key: 'baseGoodsAmount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  {
    name: 'VAT',
    key: 'baseVatAmount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  {
    name: 'Total Amount',
    key: 'baseAmount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
];

const VAT_COLUMN_META: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Financial Period',
    key: 'financialPeriod.name',
    resizable: true,
  },
  {
    name: 'Authority',
    key: 'vatAuthority.description',
    resizable: true,
  },
  {
    name: 'Business VAT Status',
    key: 'vatBusinessCategory.description',
    resizable: true,
  },
  {
    name: 'Product VAT Category',
    key: 'vatProductCategory.description',
    resizable: true,
  },
  {
    name: 'EC',
    key: 'ec',
    resizable: false,
    formatter: Formatters.BooleanFormatter,
    width: 80,
  },
  {
    name: 'Reverse Chrg',
    key: 'reverseCharge',
    resizable: false,
    formatter: Formatters.BooleanFormatter,
    width: 80,
  },
  {
    name: 'Rate',
    key: 'rate',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2, suffix: '%' },
    },
    cellClass: 'rightAlign',
    width: 100,
  },
  {
    name: 'Goods',
    key: 'baseGoods',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
  {
    name: 'VAT',
    key: 'baseVat',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
];

const getTransactionNumber = (values: any) => {
  if (!values) return 0;
  const { nominalTransactions, salesTransactions, purchaseTransactions } = values;
  if (nominalTransactions && nominalTransactions.length) {
    return nominalTransactions[0].transactionNumber;
  }
  if (salesTransactions && salesTransactions.length) {
    return salesTransactions[0].transactionNumber;
  }
  if (purchaseTransactions && purchaseTransactions.length) {
    return purchaseTransactions[0].transactionNumber;
  }
  return 0;
};

const PostedTransactionsHeaderBlock = ({
  values,
  onClose,
}: {
  values: any;
  onClose?: () => void;
} & ComponentProps<typeof BreadCrumbs>) => {
  const transactionNumber = getTransactionNumber(values);
  return (
    <>
      <BreadCrumbs title="Transaction Audit" />
      <HeaderContainer>
        <HeaderTitle
          value={`Transaction No. ${transactionNumber} - `}
          optValue={values && values.documentTypeDisplay}
          subtitle={`(${values && values.documentNumber})`}
        />
        <HeaderAction>
          <Button
            onClick={() => {
              onClose?.();
            }}
          >
            Close
          </Button>
        </HeaderAction>
      </HeaderContainer>
    </>
  );
};

const NominalPostedTransactionsForm = ({ onClose, values }: { onClose: () => void; values: any }) => {
  const columns = useDimensionColumns(COLUMN_META);

  const { foreignCurrency, salesTransactions, purchaseTransactions } = values;
  const gridHeight = Math.floor((window.innerHeight - 320) / 2);
  return (
    <div>
      <Form.FormContainer>
        <Form.Header component={PostedTransactionsHeaderBlock} onClose={onClose} />
        <Form.Form>
          {salesTransactions && salesTransactions.length ? (
            <Form.Row>
              <Form.Fieldset cols={1} collapsed={true}>
                <Form.Legend label="Sales Transactions" />
                <GridContainer alwaysActive={true}>
                  <Form.Field<IEditableDataGridProps>
                    dataid="salesTransactions"
                    component={EditableDataGrid}
                    label=""
                    columns={TRANSACTION_COLUMN_META}
                    enableCellSelect={false}
                    addBlankRow={false}
                    contentHeight={72}
                  />
                </GridContainer>
              </Form.Fieldset>
            </Form.Row>
          ) : null}
          {purchaseTransactions && purchaseTransactions.length ? (
            <Form.Row>
              <Form.Fieldset cols={1} collapsed={true}>
                <Form.Legend label="Purchase Transactions" />
                <GridContainer alwaysActive={true}>
                  <Form.Field<IEditableDataGridProps>
                    dataid="purchaseTransactions"
                    component={EditableDataGrid}
                    label=""
                    columns={PURCHASE_TRANSACTION_COLUMN_META}
                    enableCellSelect={false}
                    addBlankRow={false}
                    contentHeight={72}
                  />
                </GridContainer>
              </Form.Fieldset>
            </Form.Row>
          ) : null}
          <Form.Row>
            <Form.Fieldset cols={1} collapsed={true}>
              <Form.Legend label={`Nominal Transactions ${foreignCurrency ? '(GBP)' : ''}`} />
              <GridContainer alwaysActive={true}>
                <Form.Field<IEditableDataGridProps>
                  dataid="nominalTransactions"
                  component={EditableDataGrid}
                  label=""
                  columns={columns}
                  enableCellSelect={false}
                  addBlankRow={false}
                  contentHeight={gridHeight}
                />
              </GridContainer>
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1}>
              <Form.Legend label={`VAT Transactions ${foreignCurrency ? '(GBP)' : ''}`} />
              <GridContainer alwaysActive={true}>
                <Form.Field<IEditableDataGridProps>
                  dataid="vatTransactions"
                  component={EditableDataGrid}
                  label=""
                  columns={VAT_COLUMN_META}
                  enableCellSelect={false}
                  addBlankRow={false}
                  contentHeight={gridHeight}
                />
              </GridContainer>
            </Form.Fieldset>
          </Form.Row>
        </Form.Form>
      </Form.FormContainer>
    </div>
  );
};

export function createPostedTransactionsContainer(entity: EntityType) {
  const connector = compose(
    withContainer.formQuery(entity.instance!, false, false, 'id', (props) => getFormId(props), false),
    createForm({
      form: entity.type,
      name: entity.formTitle,
      entity: entity,
      queryFragment: entity.fragment,
      readOnly: true,
    })
  );
  return connector(NominalPostedTransactionsForm);
}

const createPostedTransactions = (entity: EntityType) => {
  return entity ? createPostedTransactionsContainer(entity) : null;
};

export default createPostedTransactions;
